/**
 * Simple in-memory cache for video URLs with expiry.
 * Cache is cleared when user revisits the Alerts page.
 *
 * Also provides request tracking to prevent stale API responses from
 * setting video URLs when the user has already selected a different alert.
 */

interface CacheEntry {
  url: string;
  timestamp: number;
}

/**
 * Build a cache key that matches the clip endpoint parameters.
 * Using only approx_capture_timestamp can collide across different window_ids.
 */
export const getVideoUrlCacheKey = (row: any): string => {
  const ts = row?.approx_capture_timestamp ?? "";
  const windowId = row?.window_id ?? "";
  return `${String(ts)}|${String(windowId)}`;
};

/**
 * Check if a video URL is a temporary/partial video clip
 */
export const isTempVideoUrl = (url: unknown): boolean => {
  return typeof url === "string" && url.toLowerCase().includes("temp");
};

// Cache storage
let videoUrlCache: Record<string, CacheEntry> = {};

/**
 * Request tracking to prevent race conditions.
 * Each time a new row is selected, we increment the request ID.
 * When an API response comes back, we check if the request ID matches
 * the current expected ID - if not, the response is stale and should be ignored.
 */
let currentVideoRequestId = 0;

/**
 * Track the row key that currently has an in-flight fetch request.
 * Used to deduplicate multiple fetch calls for the same row.
 */
let currentlyFetchingRowKey: string | null = null;

/**
 * Generate a new request ID for a video fetch operation.
 * Call this when starting a new video fetch (when selecting a new row).
 */
export const generateVideoRequestId = (): number => {
  currentVideoRequestId += 1;
  return currentVideoRequestId;
};

/**
 * Check if a request ID is still valid (matches the current expected ID).
 * Returns false if the request is stale (user has selected a different row).
 */
export const isVideoRequestValid = (requestId: number): boolean => {
  return requestId === currentVideoRequestId;
};

/**
 * Get the current video request ID without incrementing.
 * Useful for checking the current state.
 */
export const getCurrentVideoRequestId = (): number => {
  return currentVideoRequestId;
};

/**
 * Check if a fetch is already in progress for a given row key.
 */
export const isFetchInProgressForRow = (rowKey: string): boolean => {
  return currentlyFetchingRowKey === rowKey;
};

/**
 * Atomically mark a row fetch as in-progress.
 * Returns false when another fetch for the same row is already running.
 */
export const tryBeginVideoFetch = (rowKey: string): boolean => {
  if (currentlyFetchingRowKey === rowKey) {
    return false;
  }
  currentlyFetchingRowKey = rowKey;
  return true;
};

/**
 * Mark that a fetch is starting for a row.
 */
export const setFetchInProgress = (rowKey: string): void => {
  currentlyFetchingRowKey = rowKey;
};

/**
 * Clear the in-progress fetch tracking (call when fetch completes or errors).
 */
export const clearFetchInProgress = (): void => {
  currentlyFetchingRowKey = null;
};

/**
 * Invalidate all pending video requests.
 * Call this when the component unmounts to prevent state updates on unmounted components.
 * This increments the request ID so any in-flight requests will be ignored when they complete.
 */
export const invalidateAllPendingRequests = (): void => {
  currentVideoRequestId += 1;
  currentlyFetchingRowKey = null;
};

// Cache expiry time in milliseconds (5 minutes)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Get a cached video URL if it exists and hasn't expired
 */
export const getCachedVideoUrl = (cacheKey: string): string | null => {
  const entry = videoUrlCache[cacheKey];

  if (!entry) {
    return null;
  }

  // Check if entry has expired
  const now = Date.now();
  if (now - entry.timestamp > CACHE_EXPIRY_MS) {
    // Remove expired entry
    delete videoUrlCache[cacheKey];
    return null;
  }

  return entry.url;
};

/**
 * Store a video URL in the cache
 */
export const cacheVideoUrl = (cacheKey: string, url: string): void => {
  videoUrlCache[cacheKey] = {
    url,
    timestamp: Date.now(),
  };
};

/**
 * Clear the entire cache (called when user visits/revisits Alerts page).
 * Also clears in-progress fetch tracking.
 *
 * The request ID counter is intentionally NOT reset here — it must stay
 * monotonic so a stale in-flight request (whose captured ID was already
 * invalidated on unmount) can never collide with a valid future ID.
 */
export const clearVideoUrlCache = (): void => {
  videoUrlCache = {};
  currentlyFetchingRowKey = null;
};

/**
 * Remove expired entries from cache (can be called periodically if needed)
 */
export const cleanExpiredEntries = (): void => {
  const now = Date.now();
  Object.keys(videoUrlCache).forEach((key) => {
    if (now - videoUrlCache[key].timestamp > CACHE_EXPIRY_MS) {
      delete videoUrlCache[key];
    }
  });
};
